/**
 * Compute ``sha256:hex`` of a File / Blob / ArrayBuffer in the browser.
 *
 * Why hash on the client?
 *   * The Data Plane backend is idempotent on ``contentHash``: a re-upload
 *     of the same bytes (across tenants in the same org) reuses the
 *     existing asset row + already-vectorised blob. Hashing in the browser
 *     **before** the PUT lets us short-circuit duplicates without paying
 *     for the bytes to leave the device.
 *   * Server- and client-computed hashes must agree on prefix format
 *     (``sha256:``) and casing (lowercase hex). Any drift here causes the
 *     dedupe path to silently miss; the unit test in
 *     ``contentHash.test.ts`` pins the canonical encoding.
 *
 * The implementation uses ``crypto.subtle.digest`` which is available in
 * every browser StepFlow supports plus jsdom (vitest). Streaming hashing
 * (``crypto.subtle`` has no incremental API) is not needed at our file
 * sizes; we cap presigned-PUT uploads at 1 GiB on the server.
 */

/**
 * @param {ArrayBuffer | Uint8Array} buffer
 * @returns {Promise<string>} ``sha256:<lowercase hex>``
 */
export async function sha256Hex(buffer) {
  const view = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const digest = await crypto.subtle.digest('SHA-256', view);
  return `sha256:${bytesToHex(new Uint8Array(digest))}`;
}

/**
 * Hash a ``File`` (or ``Blob``) — reads the bytes once.
 *
 * @param {Blob} file
 * @returns {Promise<string>}
 */
export async function computeFileSha256(file) {
  // Quack-test: we accept anything with ``slice`` + ``size`` (Blob/File and
  // any reasonable polyfill). The actual byte read goes through a uniform
  // path so Blob and File can never disagree on the digest — older jsdom
  // builds have a buggy ``Blob.arrayBuffer`` that returns different bytes
  // than ``new Response(blob).arrayBuffer()`` on the same instance. Picking
  // *one* path keeps the dedupe contract deterministic in every runtime.
  if (
    !file
    || typeof file !== 'object'
    || typeof file.slice !== 'function'
    || typeof file.size !== 'number'
  ) {
    throw new TypeError('computeFileSha256: expected a File or Blob');
  }
  const buf = await new Response(file).arrayBuffer();
  return sha256Hex(buf);
}

function bytesToHex(bytes) {
  // Pre-built char table is ~3x faster than ``toString(16).padStart`` for
  // multi-MB inputs (matters when supervisors paste long markdown).
  const hex = new Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    hex[i] = bytes[i].toString(16).padStart(2, '0');
  }
  return hex.join('');
}

/**
 * Test affordance — exposed for unit tests that want to round-trip the hex
 * helper without exercising ``crypto.subtle``.
 */
export const _internal = { bytesToHex };
